/**
 * PipelineGraphView — visualization of a pipeline's workflow graph, with
 * optional live per-node editing.
 *
 * Presentational by default. The parent passes the backend serialized graph
 * (`{ nodes, edges, nodeConfigs, stateSchema }`); this component converts it to
 * React Flow shape (with elkjs auto-layout) via the shared
 * `convertBackendGraphToReactFlow` util and renders it inside a fixed-height
 * container using the existing `GraphFlowEditor`. The graph structure is always
 * locked (no drag/add/delete) — `readonly`.
 *
 * Node detail on click: handled by GraphFlowEditor itself — it opens the
 * existing NodeConfigModal (the Monaco-based "IDE" node modal). We do NOT roll
 * our own detail panel.
 *
 * Live editing: when `workflowUuid` is supplied (the deployed-pipeline Overview),
 * the modal opens EDITABLE. Saving a node diffs the edited config against the
 * node's current effective config and PATCHes only the delta to
 * `PATCH /workflows/{uuid}` as `nodeConfigOverrides[nodeId]`, so a few extra
 * instruction lines live-update the deployed pipeline without re-deploying.
 * Without `workflowUuid` (e.g. the marketplace preview) it stays view-only.
 *
 * Defensive: a falsy graph or one with no nodes renders a clean empty state
 * instead of crashing.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { IonIcon } from '@ionic/react';
import { scanOutline, contractOutline } from 'ionicons/icons';
import {
  GraphFlowEditor,
  convertBackendGraphToReactFlow,
} from '../../graph-workflow-editor';
import api from '../../../services/api';
import './PipelineGraphView.css';

const EMPTY_FLOW = { nodes: [], edges: [], nodeConfigs: {}, stateSchema: null };

// Keys a node override may carry (mirrors NodeConfigModal.handleSave's output).
const OVERRIDE_KEYS = ['prompt', 'extraPromptInstructions', 'tools', 'executeCode'];
const norm = (v) => (v === undefined ? null : v);

// Delta of `config` vs the node's current effective config. Only changed keys
// are sent so we never freeze a node's other (template-tracking) fields. Returns
// `{}` when nothing changed.
const overrideDelta = (config, base) => {
  const out = {};
  for (const k of OVERRIDE_KEYS) {
    if (JSON.stringify(norm(config[k])) !== JSON.stringify(norm(base?.[k]))) {
      out[k] = norm(config[k]);
    }
  }
  return out;
};

// `projectId` (optional): lets the node modal resolve a sub-workflow node's
// CHILD agent (deployed in the same project) and embed its graph.
// `embedded` (optional): render as a non-interactive embed (inside the node
// modal's sub-workflow panel) — node clicks disabled so modals never recurse,
// fullscreen button hidden. Pan/zoom stay enabled.
const PipelineGraphView = ({ graph, workflowUuid, webhookEnabled = false, onSaved, setToast, projectId = null, embedded = false }) => {
  const [flow, setFlow] = useState(EMPTY_FLOW);
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  // The deployed agent's cron schedule (null = none). Fetched separately from
  // GET /workflows/{uuid}/schedule — the workflow detail object doesn't carry
  // it. Drives the Start node's trigger badge alongside `webhookEnabled`.
  const [schedule, setSchedule] = useState(null);
  // Bumped on every fullscreen toggle so the graph re-centers + zooms to fit
  // the resized canvas.
  const [fitSignal, setFitSignal] = useState(0);

  const hasGraph = !!(graph && Array.isArray(graph.nodes) && graph.nodes.length > 0);
  const editable = !!workflowUuid;

  // Esc exits fullscreen; re-fit the view whenever the mode TOGGLES (skip the
  // initial mount so DefaultZoomOut owns first-load framing).
  const didMount = React.useRef(false);
  useEffect(() => {
    if (didMount.current) {
      setFitSignal((n) => n + 1);
    } else {
      didMount.current = true;
    }
    if (!fullscreen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  // Persist a single node's edits as a live override on the deployed pipeline.
  const handleSaveNodeConfig = useCallback(async (nodeId, config, baseConfig) => {
    if (!workflowUuid) return;
    const delta = overrideDelta(config, baseConfig);
    if (Object.keys(delta).length === 0) return; // nothing changed
    try {
      await api.patch(`/workflows/${encodeURIComponent(workflowUuid)}`, {
        nodeConfigOverrides: { [nodeId]: delta },
      });
      if (setToast) setToast({ show: true, message: 'Node updated — live on the deployed pipeline.', color: 'success' });
      if (onSaved) onSaved();
    } catch (err) {
      if (setToast) setToast({ show: true, message: `Failed to save node: ${err?.message || 'unknown error'}`, color: 'danger' });
    }
  }, [workflowUuid, onSaved, setToast]);

  // Fetch the deployed agent's cron schedule so the Start node can show a
  // trigger badge. Tied to workflowUuid; 404 = no schedule (a valid state).
  // Best-effort: a failure just means no cron badge, never blocks the graph.
  useEffect(() => {
    if (!workflowUuid) { setSchedule(null); return undefined; }
    let cancelled = false;
    api.get(`/workflows/${workflowUuid}/schedule`)
      .then((data) => { if (!cancelled) setSchedule(data?.schedule || null); })
      .catch(() => { if (!cancelled) setSchedule(null); });
    return () => { cancelled = true; };
  }, [workflowUuid]);

  // Normalized trigger summary injected into the Start node's data.
  const triggers = useMemo(() => ({
    cron: schedule?.cron || null,
    timezone: schedule?.timezone || null,
    webhook: !!webhookEnabled,
  }), [schedule, webhookEnabled]);

  // Inject the trigger summary into the Start terminal node so TerminalNode can
  // render the badge. Recomputes when the schedule resolves (badge appears once
  // the async fetch lands; syncFromProps re-syncs GraphFlowEditor).
  const nodesWithTriggers = useMemo(() => {
    if (!Array.isArray(flow.nodes)) return flow.nodes;
    return flow.nodes.map((n) =>
      n.type === 'terminal' && n.data?.terminal === 'start'
        ? { ...n, data: { ...n.data, triggers } }
        : n,
    );
  }, [flow.nodes, triggers]);

  // Convert backend graph → React Flow shape (async: elkjs auto-layout).
  // Guarded against out-of-order resolutions if `graph` changes mid-flight.
  useEffect(() => {
    let cancelled = false;
    if (!hasGraph) {
      setFlow(EMPTY_FLOW);
      return undefined;
    }
    setLoading(true);
    convertBackendGraphToReactFlow(graph)
      .then((result) => {
        if (!cancelled) setFlow(result || EMPTY_FLOW);
      })
      .catch(() => {
        if (!cancelled) setFlow(EMPTY_FLOW);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [graph, hasGraph]);

  if (!hasGraph) {
    return (
      <div className="pipeline-graph-view pipeline-graph-view--empty">
        <div className="pipeline-graph-view__empty-text">
          Graph not available for this pipeline.
        </div>
      </div>
    );
  }

  return (
    <div className={`pipeline-graph-view${fullscreen ? ' pipeline-graph-view--fullscreen' : ''}${embedded ? ' pipeline-graph-view--embedded' : ''}`}>
      <div className="pipeline-graph-view__canvas">
        {loading && (
          <div className="pipeline-graph-view__loading">Laying out graph…</div>
        )}
        {!embedded && (
          <button
            type="button"
            className="pipeline-graph-view__fs-btn"
            onClick={() => setFullscreen((v) => !v)}
            aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
            data-tooltip={fullscreen ? 'Exit full screen' : 'Full screen'}
          >
            <IonIcon icon={fullscreen ? contractOutline : scanOutline} />
          </button>
        )}
        <GraphFlowEditor
          readonly
          syncFromProps
          initialNodes={nodesWithTriggers}
          initialEdges={flow.edges}
          nodeConfigs={flow.nodeConfigs}
          stateSchema={flow.stateSchema}
          showResetPanel={false}
          showGraphInfoPanel={false}
          fitSignal={fitSignal}
          nodeConfigEditable={editable}
          onSaveNodeConfig={editable ? handleSaveNodeConfig : null}
          projectId={projectId}
          nodesClickable={!embedded}
        />
      </div>
    </div>
  );
};

export default PipelineGraphView;
